import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Message, Posit } from "@/types/posit";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { generateMessageId } from "@/utils/message";
import { trackQuestionAsked } from "@/utils/analytics";
import { jsonToFileAttachment } from "@/utils/typeConversions";
import { Json } from "@/integrations/supabase/types";
import { messagePersistence } from "@/utils/messagePersistence";

export const useChatWithPosit = (posit: Posit | null, conversationId?: string) => {
  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const positId = posit?.id;
  
  // Use provided conversationId or null (we'll create one when needed)
  const currentChatHistoryId = conversationId || null;
  
  // Track processed messages to prevent duplicates
  const messageTrackerRef = useRef<Set<string>>(new Set());
  
  // Track if we're in the middle of a URL update to prevent state loss
  const isUpdatingUrlRef = useRef(false);
  
  // Track if we're in an active chat session to prevent unnecessary database reloads
  const isActiveChatSessionRef = useRef(false);
  
  // Generate content hash for duplicate detection
  const generateContentHash = useCallback((content: string): string => {
    return content.trim().toLowerCase().replace(/\s+/g, ' ');
  }, []);

  // Messages state
  const [messages, setMessages] = useState<Message[]>([]);
  
  // Smooth streaming renderer state (decouples network from UI updates)
  const streamBufferRef = useRef<string>('');
  const streamAssistantIdRef = useRef<string | null>(null);
  const streamPlayingRef = useRef<boolean>(false);
  const streamSpeedRef = useRef<number>(60); // chars per second
  const streamLastTsRef = useRef<number>(0);
  const streamRafRef = useRef<number | null>(null);

  const stopStreamPlayback = useCallback(() => {
    streamPlayingRef.current = false;
    if (streamRafRef.current) {
      cancelAnimationFrame(streamRafRef.current);
      streamRafRef.current = null;
    }
  }, []);

  // Cleanup function for useEffect
  const cleanupStreamResources = useCallback(() => {
    stopStreamPlayback();
    streamBufferRef.current = '';
    streamAssistantIdRef.current = null;
    streamSpeedRef.current = 60;
    streamLastTsRef.current = 0;
  }, [stopStreamPlayback]);

  const playStreamFrame = useCallback((ts: number) => {
    if (!streamPlayingRef.current) return;
    if (!streamLastTsRef.current) streamLastTsRef.current = ts;
    const elapsed = ts - streamLastTsRef.current;
    const charsToEmit = Math.floor((elapsed / 1000) * streamSpeedRef.current);
    if (charsToEmit > 0 && streamBufferRef.current.length > 0 && streamAssistantIdRef.current) {
      let emit = streamBufferRef.current.slice(0, charsToEmit);
      // Remove any confidence labels from streamed text before it hits the UI
      emit = emit
        .replace(/\(confidence:\s*(high|medium|low)\)/gi, '')
        .replace(/confidence:\s*(high|medium|low)/gi, '')
        // collapse only spaces/tabs, preserve newlines for markdown
        .replace(/[ \t]{2,}/g, ' ');
      streamBufferRef.current = streamBufferRef.current.slice(charsToEmit);
      const targetId = streamAssistantIdRef.current;
      setMessages(prev => prev.map(m => m.id === targetId ? { ...m, content: (m.content || '') + emit } : m));
      streamLastTsRef.current = ts;
      // Small natural pause after punctuation
      if (/[\.?!]/.test(emit.slice(-1))) {
        setTimeout(() => { streamRafRef.current = requestAnimationFrame(playStreamFrame); }, 140);
        return;
      }
    }
    streamRafRef.current = requestAnimationFrame(playStreamFrame);
  }, [setMessages]);
  
  // Prewarm caches on posit load to reduce first-response latency
  useEffect(() => {
    const prewarm = async () => {
      try {
        if (!posit?.id) return;
        const { data } = await supabase.auth.getSession();
        const accessToken = data?.session?.access_token;
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
        const fnUrl = `${supabaseUrl}/functions/v1/chat-with-posit-v4-unified`;
        await fetch(fnUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': anonKey,
            'Authorization': `Bearer ${accessToken || anonKey}`
          },
          body: JSON.stringify({ posit_id: posit.id, query: 'warmup', prewarm: true })
        }).catch(() => void 0);
      } catch (error) {
        console.warn('Cache prewarming failed:', error);
      }
    };
    prewarm();
  }, [positId]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      cleanupStreamResources();
    };
  }, [cleanupStreamResources]);

  // Reset messages when conversation changes (but not during active chat URL updates)
  useEffect(() => {
    if (!isUpdatingUrlRef.current) {
      setMessages([]);
      messageTrackerRef.current.clear();
      isActiveChatSessionRef.current = false;
    }
  }, [currentChatHistoryId, positId]);

  // Ensure chat history exists (only for authenticated users)
  const ensureChatHistory = useCallback(async (): Promise<string | null> => {
    if (!user?.id || !positId) {
      return null;
    }

    if (conversationId) {
      return conversationId;
    }

    // Create new chat history
    const { data, error } = await supabase
      .from('chat_history')
      .insert({
        posit_id: positId,
        user_id: user.id,
        created_at: new Date().toISOString(),
        last_message_at: new Date().toISOString(),
      })
      .select('id')
      .single();

    if (error) {
      throw error;
    }

    
    // Note: We no longer redirect immediately here. The URL will be updated after message processing.
    
    return data.id;
  }, [user?.id, positId, conversationId]);

  // Load messages from database (only for authenticated users with chat history)
  const { data: messagesData, refetch: refetchMessages } = useQuery({
    queryKey: ["messages", posit?.id, currentChatHistoryId],
    queryFn: async () => {
      if (!posit || !currentChatHistoryId || !user) return [];
      
      // First, check if the current user has access to this chat history
      const { data: chatHistoryCheck, error: historyError } = await supabase
        .from("chat_history")
        .select("id, user_id")
        .eq("id", currentChatHistoryId)
        .maybeSingle();
      
      // If no chat history found OR user doesn't own it, redirect to new chat
      if (historyError || !chatHistoryCheck || chatHistoryCheck.user_id !== user.id) {
        
        // Don't redirect if we're in the middle of a URL update
        if (isUpdatingUrlRef.current) {
          return [];
        }
        
        const newUrl = `/chat/${posit.id}`;
        navigate(newUrl, { replace: true });
        return [];
      }
      
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("chat_history_id", currentChatHistoryId)
        .order("created_at", { ascending: true });
      
      if (error) {
        throw error;
      }
      
      return data.map(msg => ({
        id: msg.id,
        role: msg.role as "user" | "assistant",
        content: msg.content,
        created_at: msg.created_at,
        created_by: msg.created_by,
        attachments: msg.attachments ? msg.attachments.map(jsonToFileAttachment) : [],
        citations: (msg as any).citations || [],
      }));
    },
    enabled: !!posit && !!currentChatHistoryId && !!user,
  });

  // Update messages when data changes (only on initial load, not during active chat)
  useEffect(() => {
    if (messagesData) {
      // Only load from database if we don't have messages yet AND we're not in an active chat session
      // This prevents database reloads from overwriting active chat UI state during URL updates
      setMessages(prev => {
        if (prev.length === 0) {
          return messagesData;
        } else {
          return prev; // Keep current UI state
        }
      });
    }
  }, [messagesData, currentChatHistoryId]);

  // SIMPLIFIED: Duplicate message detection (only called on submit)
  const isDuplicateMessage = (content: string) => {
    // Use the same hash generation as markMessageAsProcessed for consistency
    const contentHash = generateContentHash(content);
    return messageTrackerRef.current.has(contentHash);
  };

  // Mark message as processed
  const markMessageAsProcessed = (content: string) => {
    const contentHash = generateContentHash(content);
    messageTrackerRef.current.add(contentHash);
  };

  // MODIFIED: Handle query with support for unauthenticated users
  const handleQuery = useCallback(async (content: string) => {
    if (!posit) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No Posit loaded to chat with. Please try refreshing the page.",
      });
      return;
    }

    // Check for duplicate only when actually submitting
    if (isDuplicateMessage(content) || isLoading) {
      return;
    }

    // Create user message outside try block so it's accessible in catch
    const userMessage: Message = {
      id: generateMessageId(),
      role: "user",
      content,
      created_at: new Date().toISOString(),
      created_by: user?.id || null,
      status: 'sending',
    };

    try {
      setIsLoading(true);
      
      // Mark as active chat session to prevent database reloads during URL updates
      isActiveChatSessionRef.current = true;
      
      // Show user message immediately for responsive UI
      setMessages(prev => [...prev, userMessage]);
      markMessageAsProcessed(content);
      
      // Start AI processing and database operations in parallel
      
      // Create or reuse a single chat history ID to avoid duplication and mismatched saves
      const sharedChatHistoryId = user ? await ensureChatHistory() : null;

      const [streamResult, chatHistoryId] = await Promise.all([
        // AI Processing via streaming for immediate feedback
        (async () => {
          const { data: sessionData } = await supabase.auth.getSession();
          const accessToken = sessionData?.session?.access_token;

          // Create a streaming assistant placeholder + init smooth renderer
          const assistantId = generateMessageId();
          setMessages(prev => [...prev, { id: assistantId, role: 'assistant', content: '', created_at: new Date().toISOString(), created_by: null }]);
          streamAssistantIdRef.current = assistantId;
          streamBufferRef.current = '';
          streamPlayingRef.current = false;
          streamLastTsRef.current = 0;

          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
          const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
          const fnUrl = `${supabaseUrl}/functions/v1/chat-with-posit-v4-unified`;

          // Provide anonymous preview session id when not logged in so the server can count preview usage
          let previewSessionId: string | undefined;
          try {
            if (!user?.id) {
              const key = 'posit_preview_session';
              const existing = localStorage.getItem(key);
              if (existing) {
                previewSessionId = existing;
              } else {
                const sid = (globalThis as any).crypto?.randomUUID ? crypto.randomUUID() : `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`;
                localStorage.setItem(key, sid);
                previewSessionId = sid;
              }
            }
          } catch (_) {}
          const res = await fetch(fnUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': anonKey,
              'Authorization': `Bearer ${accessToken || anonKey}`
            },
            body: JSON.stringify({
              query: content,
              posit_id: posit.id,
              user_id: user?.id || '',
              chat_history_id: sharedChatHistoryId || undefined,
              assistant_message_id: assistantId,
              stream: true,
              client_persist: false,
              preview_session_id: previewSessionId
            })
          });

          if (!res.ok || !res.body) throw new Error('AI streaming request failed');

          const reader = res.body.getReader();
          const decoder = new TextDecoder();

          let finalMeta: any = null;
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              const payload = line.slice(6).trim();
              if (!payload) continue;
              if (payload === '[DONE]') break;
              try {
                const data = JSON.parse(payload);
                if (data.error) {
                  throw new Error(String(data.error));
                }
                if (data.chunk) {
                  // Buffer chunk and let rAF playback render smoothly
                  streamBufferRef.current += String(data.chunk);
                  if (!streamPlayingRef.current) {
                    streamPlayingRef.current = true;
                    streamLastTsRef.current = 0;
                    if (streamRafRef.current) cancelAnimationFrame(streamRafRef.current);
                    streamRafRef.current = requestAnimationFrame(playStreamFrame);
                  }
                }
                if (data.final) {
                  finalMeta = data;
                }
              } catch (parseError) {
            // ignore keepalive/non-JSON lines, but log for debugging
            if (parseError instanceof Error && !parseError.message.includes('JSON')) {
              console.warn('Stream parsing error:', parseError);
            }
          }
            }
          }
          // Stop smooth playback and flush remaining buffer
          stopStreamPlayback();
          if (streamBufferRef.current && streamAssistantIdRef.current) {
            const rest = streamBufferRef.current
              .replace(/\(confidence:\s*(high|medium|low)\)/gi, '')
              .replace(/confidence:\s*(high|medium|low)/gi, '')
              // collapse only spaces/tabs, preserve newlines for markdown
              .replace(/[ \t]{2,}/g, ' ');
            const targetId = streamAssistantIdRef.current;
            setMessages(prev => prev.map(m => m.id === targetId ? { ...m, content: (m.content || '') + rest } : m));
            streamBufferRef.current = '';
          }

          // Final safety scrub on the full assistant message content
          if (streamAssistantIdRef.current) {
            const targetId = streamAssistantIdRef.current;
            setMessages(prev => prev.map(m => {
              if (m.id !== targetId) return m;
              const cleaned = String(m.content || '')
                .replace(/\(confidence:\s*(high|medium|low)\)/gi, '')
                .replace(/confidence:\s*(high|medium|low)/gi, '')
                // collapse only spaces/tabs, preserve newlines for markdown
                .replace(/[ \t]{2,}/g, ' ');
              return { ...m, content: cleaned };
            }));
          }

          // Attach citations if provided
          if (finalMeta?.citations) {
            setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, citations: finalMeta.citations } as any : m));
          }

          return { ok: true };
        })(),
        
        // Database Operations (Background - ~200ms)
        (async () => {
          if (!user) {
            return null;
          }
          
          const chatHistoryId = sharedChatHistoryId || (await ensureChatHistory());

          // Save user message in background
          supabase.from("chat_messages").insert({
            id: userMessage.id,
            posit_id: posit.id,
            chat_history_id: chatHistoryId,
            role: "user",
            content,
            created_by: user.id,
            source: 'posit_query'
          }).then(({ error }) => {
            if (error) {
            } else {
            }
          });

          // After at least 3 messages, generate/update a 5-words-or-less short summary in the background
          try {
            const { count } = await supabase
              .from('chat_messages')
              .select('*', { count: 'exact', head: true })
              .eq('chat_history_id', chatHistoryId)
              .eq('source', 'posit_query');
            if ((count || 0) >= 3) {
              const { data: msgs } = await supabase
                .from('chat_messages')
                .select('role, content')
                .eq('chat_history_id', chatHistoryId)
                .order('created_at', { ascending: true })
                .limit(30);
              if (msgs && msgs.length > 0) {
                const { data: shortRes, error: shortErr } = await supabase.functions.invoke('generate-chat-summary', {
                  body: { messages: msgs, mode: 'short' }
                });
                const shortSummary: string | undefined = (shortRes as any)?.short_summary || (shortRes as any)?.summary;
                if (!shortErr && shortSummary) {
                  const cleaned = String(shortSummary)
                    .replace(/^[-•\s]+/g, '')
                    .split(/\s+/)
                    .slice(0, 5)
                    .join(' ')
                    .trim();
                  await supabase
                    .from('chat_history')
                    .update({ short_summary: cleaned })
                    .eq('id', chatHistoryId);
                }
              }
            }
          } catch (error) {
            console.warn('Summary generation failed:', error);
          }

          return chatHistoryId;
        })()
      ]);

      // Response already streamed into UI and saved by server
      
      // Analytics tracking in background (non-blocking)
      if (user) {
        trackQuestionAsked(posit.id, user.id).catch(analyticsError => {
        });
      }


      // Ensure a conversation summary exists even if the chat ends before 3 messages
      if (user && chatHistoryId) {
        (async () => {
          try {
            // If summary already exists, skip
            const { data: hist } = await supabase
              .from('chat_history')
              .select('summary')
              .eq('id', chatHistoryId)
              .maybeSingle();
            if (hist?.summary) return;

            // Pull a small window of messages to summarize
            const { data: msgs } = await supabase
              .from('chat_messages')
              .select('role, content')
              .eq('chat_history_id', chatHistoryId)
              .order('created_at', { ascending: true })
              .limit(30);
            if (!msgs || msgs.length === 0) return;

            const { data: sumRes, error: sumErr } = await supabase.functions.invoke('generate-chat-summary', {
              body: { messages: msgs }
            });
            const fullSummary: string | undefined = (sumRes as any)?.summary;
            if (!sumErr && fullSummary) {
              await supabase
                .from('chat_history')
                .update({ summary: fullSummary })
                .eq('id', chatHistoryId);
            }
          } catch (error) {
            console.warn('Full summary generation failed:', error);
          }
        })();
      }
      
      // Update URL if this was the first message and we created a new chat history
      if (user && chatHistoryId && !conversationId) {
        const newUrl = `/chat/${posit.id}/${chatHistoryId}`;
        isUpdatingUrlRef.current = true;
        // Add a small delay to ensure database transactions are complete
        setTimeout(() => {
          navigate(newUrl, { replace: true });
          // Reset the flag after navigation
          setTimeout(() => {
            isUpdatingUrlRef.current = false;
          }, 50);
        }, 100);
      }
      
      // Mark user message as sent and clean up from failed messages
      setMessages(prev => prev.map(msg => 
        msg.id === userMessage.id 
          ? { ...msg, status: 'sent' as const }
          : msg
      ));
      
      // Remove from failed messages localStorage if it was there
      messagePersistence.removeFailedMessage(userMessage.id);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to send message. Please try again.";
      
      // Clean up assistant placeholder if streaming started
      if (streamAssistantIdRef.current) {
        const targetId = streamAssistantIdRef.current;
        stopStreamPlayback();
        setMessages(prev => prev.filter(m => m.id !== targetId));
        streamAssistantIdRef.current = null;
        streamBufferRef.current = '';
      }

      // Mark the user message as failed instead of removing it
      setMessages(prev => prev.map(msg => 
        msg.id === userMessage.id 
          ? { ...msg, status: 'failed' as const, error: errorMessage, retryCount: (msg.retryCount || 0) + 1 }
          : msg
      ));
      
      // Save failed message to localStorage for recovery
      if (posit?.id) {
        messagePersistence.saveFailedMessage(userMessage, posit.id, errorMessage);
      }
      
      // Show error toast
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
      
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [posit, user, isLoading, ensureChatHistory, toast, navigate, conversationId]);

  return {
    messages,
    content,
    setContent,
    isLoading,
    handleQuery,
    refetchMessages,
  };
};
